/**
 * Follow-up stream processing utilities
 */

// Removed stream status config imports - using string literals
import { safelyUpdateThreadState } from "./threadStateManagement";
import { safelyUpdateStreamingMessage } from "./streamUtils";

/**
 * Process follow-up stream chunk
 * @param {Object} params - Processing parameters
 * @returns {Object} - Processing result
 */
export function processFollowUpChunk({
  followUpChunk,
  modelProvider,
  followUpBuffer,
  messageBuffer,
  updateMessageState,
  currentMessageId,
  followUpMessageId,
  threadStore,
  threadId = null,
  actualProviderName = null,
  modelName = null,
}) {
  let followUpContent = "";
  let followUpToolCalls = [];
  let isToolUseResponse = false;

  if (
    followUpChunk.choices &&
    followUpChunk.choices.length > 0 &&
    followUpChunk.choices[0].delta
  ) {
    const followUpDelta = followUpChunk.choices[0].delta;

    if (followUpDelta.content) {
      followUpContent = followUpDelta.content;
    }

    if (followUpDelta.tool_calls) {
      followUpToolCalls = followUpDelta.tool_calls;
    }
  } else if (
    modelProvider === "anthropic" &&
    followUpChunk.delta &&
    followUpChunk.delta.text
  ) {
    followUpContent = followUpChunk.delta.text;
  } else if (
    modelProvider === "anthropic" &&
    followUpChunk.content &&
    Array.isArray(followUpChunk.content)
  ) {
    for (const block of followUpChunk.content) {
      if (block.type === "text") {
        followUpContent = block.text;
      } else if (block.type === "tool_use") {
        isToolUseResponse = true;
        followUpToolCalls.push({
          id: block.id,
          type: "function",
          function: {
            name: block.name,
            arguments:
              typeof block.input === "object"
                ? JSON.stringify(block.input)
                : "{}",
          },
        });
      }
    }
  }

  if (followUpContent) {
    const updatedFollowUpBuffer = followUpBuffer + followUpContent;

    if (updateMessageState && followUpMessageId) {
      const fullContent =
        messageBuffer +
        (updatedFollowUpBuffer ? "\n\n" + updatedFollowUpBuffer : "");

      // Use batching for follow-up stream updates to prevent browser crashes during web search streaming
      safelyUpdateStreamingMessage(
        updateMessageState,
        followUpMessageId,
        fullContent,
        "streaming",
        true, // Force batching
        {
          provider: actualProviderName || modelProvider,
          model: modelName,
          isFollowUpContent: true,
          toolCallMessageId: currentMessageId,
        },
        threadId,
        "assistant"
      );
    }

    if (threadStore) {
      safelyUpdateThreadState(threadStore, {
        currentMessageContent:
          messageBuffer +
          (updatedFollowUpBuffer ? "\n\n" + updatedFollowUpBuffer : ""),
        status: "streaming",
      });

      const setStoreState =
        typeof threadStore === "function" ? threadStore : threadStore.setState;
      const fullContent =
        messageBuffer +
        (updatedFollowUpBuffer ? "\n\n" + updatedFollowUpBuffer : "");

      setStoreState((state) => ({
        ...state,
        messages:
          state.messages?.map((msg) =>
            msg.id === followUpMessageId
              ? {
                  ...msg,
                  content: fullContent,
                  updated_at: new Date().toISOString(),
                  isStreaming: true,
                  metadata: {
                    ...msg.metadata,
                    // CRITICAL FIX: Ensure provider and model are always set
                    provider:
                      actualProviderName ||
                      modelProvider ||
                      msg.metadata?.provider,
                    model: modelName || msg.metadata?.model,
                    isFollowUpContent: true,
                    toolCallMessageId: currentMessageId,
                    hasContentInFollowUp: true,
                  },
                }
              : msg
          ) || [],
      }));
    }

    return {
      followUpContent,
      followUpBuffer: updatedFollowUpBuffer,
      followUpToolCalls,
      isToolUseResponse,
    };
  }

  return {
    followUpContent: "",
    followUpBuffer,
    followUpToolCalls,
    isToolUseResponse,
  };
}

/**
 * Handle follow-up stream error
 * @param {Object} params - Error handling parameters
 */
export function handleFollowUpStreamError({
  error,
  updateMessageState,
  currentMessageId,
  followUpMessageId,
  messageBuffer,
  followUpBuffer,
  threadStore,
  threadId = null,
}) {
  if (error) {
    console.error("Error in follow-up stream:", error);

    if (updateMessageState && followUpMessageId) {
      updateMessageState({
        id: followUpMessageId,
        role: "assistant",
        content:
          messageBuffer + (followUpBuffer ? "\n\n" + followUpBuffer : ""),
        isStreaming: false,
        isStreamComplete: true,
        error: `Error in follow-up completion: ${error.message || "Unknown error"}`,
        messageStatus: "error",
        ...(threadId ? { thread_id: threadId } : {}),
      });
    }

    if (threadStore) {
      safelyUpdateThreadState(threadStore, {
        error: `Error in follow-up completion: ${error.message || "Unknown error"}`,
        isStreaming: false,
        streamStatus: "FAILED",
      });
    }
  }
}

/**
 * Handle follow-up stream creation error
 * @param {Object} params - Error handling parameters
 */
export function handleFollowUpStreamCreationError({
  streamError,
  updateMessageState,
  currentMessageId,
  followUpMessageId,
  messageBuffer,
  threadStore,
  toolResults,
  threadId = null,
}) {
  console.error("Error creating follow-up stream:", streamError);

  if (updateMessageState && followUpMessageId) {
    updateMessageState({
      id: followUpMessageId,
      role: "assistant",
      content: messageBuffer,
      isStreaming: false,
      isStreamComplete: true,
      error: `Error creating follow-up stream: ${streamError.message || "Unknown error"}`,
      messageStatus: "error",
      ...(threadId ? { thread_id: threadId } : {}),
    });
  }

  if (threadStore) {
    safelyUpdateThreadState(threadStore, {
      error: `Error creating follow-up stream: ${streamError.message || "Unknown error"}`,
      isStreaming: false,
      streamStatus: "FAILED",
      toolResults,
    });
  }
}

/**
 * Format follow-up messages for different providers
 * @param {Array} toolResults - Tool execution results
 * @param {string} modelProvider - The model provider name
 * @param {Array} messages - Original messages
 * @param {string} enhancedInstructions - Enhanced instructions
 * @param {Object} providerFactory - Provider factory instance
 * @param {string} modelName - Model name
 * @returns {Array} - Formatted messages
 */
export function formatFollowUpMessages(
  toolResults,
  modelProvider,
  messages,
  enhancedInstructions,
  providerFactory,
  modelName
) {
  if (modelProvider === "anthropic") {
    // Backend provider system doesn't need provider factory
    // Handle Anthropic message formatting directly
    const formattedMessages = [];

    if (enhancedInstructions) {
      formattedMessages.push({
        role: "system",
        content: enhancedInstructions,
      });
    }

    const userMessages = messages.filter((m) => m.role === "user");
    if (userMessages.length > 0) {
      const lastUserMessage = userMessages[userMessages.length - 1];
      formattedMessages.push(lastUserMessage);
    }

    const assistantMessages = messages.filter((m) => m.role === "assistant");
    const accumulatedContent =
      assistantMessages.length > 0
        ? assistantMessages[assistantMessages.length - 1].content || ""
        : "";

    const assistantToolCallsMessage = {
      role: "assistant",
      content: accumulatedContent,
      tool_calls: toolResults.map(({ toolCall }) => ({
        id: toolCall.id,
        type: "function",
        function: {
          name: toolCall.function?.name || "unknown_function",
          arguments: toolCall.function?.arguments || "{}",
        },
      })),
    };

    formattedMessages.push(assistantToolCallsMessage);

    const toolMessages = toolResults.map(({ toolCall, result }) => {
      const processedResult =
        typeof result === "object"
          ? JSON.stringify(result, null, 2)
          : String(result || "");

      return {
        role: "tool",
        tool_call_id: toolCall.id,
        content: processedResult,
      };
    });

    formattedMessages.push(...toolMessages);

    return formattedMessages;
  } else {
    return toolResults.map(({ toolCall, result }) => ({
      role: "tool",
      tool_call_id: toolCall.id,
      content:
        typeof result === "string" ? result : JSON.stringify(result, null, 2),
    }));
  }
}
